'use client';

import { c as _c } from "react/compiler-runtime";
import { type MotionValue, motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
type ResponsiveConfig = {
  mobile?: {
    speed?: number;
  };
  tablet?: {
    speed?: number;
  };
  breakpoints?: {
    mobile?: number;
    tablet?: number;
  };
};
type ScrollOffset = 'start start' | 'start center' | 'start end' | 'center start' | 'center center' | 'center end' | 'end start' | 'end center' | 'end end' | any;
type ParallaxTransforms = {
  x?: MotionValue<string | number>;
  y?: MotionValue<string | number>;
};
type ParallaxProps = {
  children: ReactNode;
  speed?: number;
  direction?: 'x' | 'y' | 'both';
  offset?: [ScrollOffset, ScrollOffset];
  responsive?: ResponsiveConfig;
  className?: string;
  initialOffset?: {
    x?: string | number;
    y?: string | number;
  };
  disabled?: boolean;
};
export function Parallax(t0) {
  const $ = _c(30);
  const {
    children,
    speed: t1,
    direction: t2,
    offset: t3,
    responsive,
    className,
    initialOffset,
    disabled: t4
  } = t0;
  const speed = t1 === undefined ? 0.5 : t1;
  const direction = t2 === undefined ? "y" : t2;
  let t5;
  if ($[0] !== t3) {
    t5 = t3 === undefined ? ["start end", "end start"] : t3;
    $[0] = t3;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const offset = t5;
  const disabled = t4 === undefined ? false : t4;
  const ref = useRef(null);
  let t6;
  if ($[2] !== speed) {
    t6 = {
      speed
    };
    $[2] = speed;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const [currentConfig, setCurrentConfig] = useState(t6);
  const shouldReduceMotion = useReducedMotion();
  let t7;
  let t8;
  if ($[4] !== responsive || $[5] !== speed) {
    t7 = () => {
      if (!responsive) {
        return;
      }
      const checkResponsive = () => {
        const width = window.innerWidth;
        const mobileBreakpoint = responsive.breakpoints?.mobile ?? 768;
        const tabletBreakpoint = responsive.breakpoints?.tablet ?? 1024;
        if (width < mobileBreakpoint && responsive.mobile) {
          setCurrentConfig({
            speed: responsive.mobile.speed ?? speed
          });
        } else {
          if (width < tabletBreakpoint && responsive.tablet) {
            setCurrentConfig({
              speed: responsive.tablet.speed ?? speed
            });
          } else {
            setCurrentConfig({
              speed
            });
          }
        }
      };
      checkResponsive();
      window.addEventListener("resize", checkResponsive);
      return () => window.removeEventListener("resize", checkResponsive);
    };
    t8 = [speed, responsive];
    $[4] = responsive;
    $[5] = speed;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  const t9 = offset as [ScrollOffset, ScrollOffset];
  let t10;
  if ($[8] !== t9) {
    t10 = {
      target: ref,
      offset: t9
    };
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const {
    scrollYProgress,
    scrollXProgress
  } = useScroll(t10);
  const baseOffsetY = initialOffset?.y ?? 0;
  const baseOffsetX = initialOffset?.x ?? 0;
  const getEndValue = _temp;
  const t11 = getEndValue(baseOffsetY, currentConfig.speed * -100);
  let t12;
  if ($[10] !== baseOffsetY || $[11] !== t11) {
    t12 = [baseOffsetY, t11];
    $[10] = baseOffsetY;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const outputRangeY = t12;
  const t13 = getEndValue(baseOffsetX, currentConfig.speed * -100);
  let t14;
  if ($[13] !== baseOffsetX || $[14] !== t13) {
    t14 = [baseOffsetX, t13];
    $[13] = baseOffsetX;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const outputRangeX = t14;
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [0, 1];
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const y = useTransform(scrollYProgress, t15, outputRangeY);
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [0, 1];
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const x = useTransform(scrollXProgress, t16, outputRangeX);
  let transforms;
  if ($[18] !== direction || $[19] !== disabled || $[20] !== shouldReduceMotion || $[21] !== x || $[22] !== y) {
    transforms = {};
    if (!shouldReduceMotion && !disabled) {
      if (direction === "y" || direction === "both") {
        transforms.y = y;
      }
      if (direction === "x" || direction === "both") {
        transforms.x = x;
      }
    }
    $[18] = direction;
    $[19] = disabled;
    $[20] = shouldReduceMotion;
    $[21] = x;
    $[22] = y;
    $[23] = transforms;
  } else {
    transforms = $[23];
  }
  let t17;
  if ($[24] !== className) {
    t17 = cn(className);
    $[24] = className;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== children || $[27] !== t17 || $[28] !== transforms) {
    t18 = <motion.div ref={ref} style={transforms} className={t17}>{children}</motion.div>;
    $[26] = children;
    $[27] = t17;
    $[28] = transforms;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  return t18;
}
function _temp(baseValue, speedOffset) {
  if (typeof baseValue === "number") {
    return baseValue + speedOffset;
  }
  return `calc(${baseValue} + ${speedOffset}px)`;
}